import React from 'react'
import styled, { DefaultTheme } from 'styled-components'
import { get } from '@ally/metronome-ui'
import { AccountDetails } from '../../types'

interface AccountDetailsProps {
  account: AccountDetails
  detailItems: React.ReactElement[]
}

const DisplayTopCard = styled('div')`
  display: block;
  position: relative;
  padding-left: 30px;
  padding-right: 30px;
  padding-bottom: ${get.space('xs')};
  ${get.media.up('md')} {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
  }
  @media print {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
  }
`

const StyledTopCard = styled('div')<{
  accountType: keyof DefaultTheme['colors']
}>`
  ${({ accountType, theme: { colors } }): string =>
    `
  & {
    position: relative;
    &:before {
      content: '';
      background-color: ${colors[accountType]};
      border-color: ${colors[accountType]};
      width: 7px;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
    }
    &:after {
      content: '';
      margin: 0 auto;
      margin-right: 30px;
      margin-left: 30px;
      display: block;
      border-bottom: 1px solid ${colors['slate-2']};
    }
  }

`};
`
// Nickname and Acct Number group
const TopCardItemLeft = styled('div')`
  text-align: left;
  padding-top: ${get.space('xs')};
  padding-right: ${get.space('xl')};
  margin-right: ${get.space('lg')};
  ${get.media.between('sm', 'sm')} {
    margin-right: 0;
    padding-right: 0;
  }
  @media print {
    padding-right: ${get.space('xl')};
    margin-right: ${get.space('lg')};
  }
`
// Details group
const TopCardItemRight = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  color: ${get.colors('slate-5')};
  ${get.media.up('lg')} {
    width: auto;
    flex-direction: row;
    justify-content: space-between;
  }
  @media print {
    width: auto;
    flex-direction: row;
    justify-content: space-between;
  }
  padding-top: ${get.space('sm')};
`
// individual detail
const TopCardItem = styled('div')`
  text-align: left;
  &:not(:first-child) {
    margin-top: ${get.space('sm')};
  }
  ${get.media.up('lg')} {
    text-align: right;
    &:not(:first-child) {
      margin-top: ${get.space('none')};
      margin-left: ${get.space('lg')};
    }
  }
  @media print {
    text-align: right;
    &:not(:first-child) {
      margin-top: ${get.space('none')};
      margin-left: ${get.space('sm')};
    }
  }
`

export const TopCard: React.FC<AccountDetailsProps> = ({
  account,
  children,
  detailItems,
}) => {
  // istanbul ignore next // TO DO METRONOME UPGRADE REMOVE IGNORE AFTER WE FIX TESTS FAILING
  // Use IRA in the case of IRA OSA or IRA CD as those accounts use their respective account types (sda and cda)
  const accountType = account.retirementAccountIndicator
    ? 'ira'
    : account.accountType.toLowerCase()

  return (
    <StyledTopCard
      data-testid="coloredBar"
      accountType={accountType as keyof DefaultTheme['colors']}
    >
      <DisplayTopCard>
        <TopCardItemLeft>{children}</TopCardItemLeft>

        <TopCardItemRight>
          {detailItems.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <TopCardItem key={i}>{item}</TopCardItem>
          ))}
        </TopCardItemRight>
      </DisplayTopCard>
    </StyledTopCard>
  )
}
